const ONLYFINDZ_CONSENT_KEY = 'cookie_consent';
const SHOPIFY_CONSENT_FEATURE = { name: 'consent-tracking-api', version: '0.1' };

function normalizeConsent(consent) {
  return {
    preferences: Boolean(consent && consent.preferences),
    analytics: Boolean(consent && consent.analytics),
    marketing: Boolean(consent && consent.marketing),
  };
}

function parseShopifyConsentValue(value) {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
}

function mapShopifyConsent(consent) {
  if (!consent) return null;

  const storedConsent = ConsentStorage.get();

  const mapped = {
    analytics: parseShopifyConsentValue(consent.analytics),
    marketing: parseShopifyConsentValue(consent.marketing),
  };

  if (mapped.analytics === null && mapped.marketing === null) {
    return null;
  }

  return {
    preferences: Boolean(storedConsent && storedConsent.preferences),
    analytics: mapped.analytics === true,
    marketing: mapped.marketing === true,
  };
}

function getTrackingConsentPayload(consent) {
  const payload = normalizeConsent(consent);
  return {
    analytics: payload.analytics,
    marketing: payload.marketing,
  };
}

function serializeConsent(consent) {
  return JSON.stringify(normalizeConsent(consent));
}

function consentMatches(left, right) {
  return serializeConsent(left) === serializeConsent(right);
}

function getLocalQaConsentOverride() {
  try {
    const params = new URLSearchParams(window.location.search);
    const qaConsent = params.get('qa_consent');

    if (qaConsent === 'accepted') {
      return { preferences: true, analytics: true, marketing: true };
    }

    if (qaConsent === 'rejected') {
      return { preferences: false, analytics: false, marketing: false };
    }
  } catch (_) {}

  return null;
}

const LOCAL_QA_CONSENT_OVERRIDE = getLocalQaConsentOverride();

/* localStorage can fail in previews or strict browser settings. Persist a
   local mirror, but never treat it as the sole source of truth. */
const ConsentStorage = {
  get() {
    try {
      const ls = window.localStorage.getItem(ONLYFINDZ_CONSENT_KEY);
      if (ls) return normalizeConsent(JSON.parse(ls));
    } catch (_) {}

    try {
      const match = document.cookie.match(
        new RegExp('(?:^|;\\s*)' + ONLYFINDZ_CONSENT_KEY + '=([^;]+)')
      );
      if (match) return normalizeConsent(JSON.parse(decodeURIComponent(match[1])));
    } catch (_) {}

    return null;
  },

  set(payload) {
    const normalized = normalizeConsent(payload);

    try {
      window.localStorage.setItem(ONLYFINDZ_CONSENT_KEY, JSON.stringify(normalized));
    } catch (_) {}

    try {
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie =
        ONLYFINDZ_CONSENT_KEY +
        '=' +
        encodeURIComponent(JSON.stringify(normalized)) +
        '; expires=' +
        expires +
        '; path=/; SameSite=Lax';
    } catch (_) {}
  },
};

if (LOCAL_QA_CONSENT_OVERRIDE) {
  document.documentElement.classList.add('onlyfindz-qa-consent');
  ConsentStorage.set(LOCAL_QA_CONSENT_OVERRIDE);

  const removeQaConsentBanners = () => {
    document
      .querySelectorAll(
        'cookie-consent-banner, .cookie-consent-banner, .shopify-pc__banner, .shopify-pc__prefs__dialog, [data-shopify-consent-banner], [role="alertdialog"]'
      )
      .forEach((node) => {
        node.setAttribute('hidden', 'hidden');
        node.setAttribute('aria-hidden', 'true');
        if (typeof node.remove === 'function') node.remove();
      });
  };

  const qaHideStyle = document.createElement('style');
  qaHideStyle.textContent = `
    .onlyfindz-qa-consent cookie-consent-banner,
    .onlyfindz-qa-consent .cookie-consent-banner,
    .onlyfindz-qa-consent .shopify-pc__banner,
    .onlyfindz-qa-consent .shopify-pc__prefs__dialog,
    .onlyfindz-qa-consent [data-shopify-consent-banner],
    .onlyfindz-qa-consent [role="alertdialog"] {
      display: none !important;
      visibility: hidden !important;
    }
  `;
  document.head.appendChild(qaHideStyle);
  removeQaConsentBanners();
  new MutationObserver(removeQaConsentBanners).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function getCustomerPrivacyApi() {
  return window.Shopify && window.Shopify.customerPrivacy ? window.Shopify.customerPrivacy : null;
}

function loadCustomerPrivacyApi() {
  const api = getCustomerPrivacyApi();
  if (api) return Promise.resolve(api);

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    window.Shopify.loadFeatures([SHOPIFY_CONSENT_FEATURE], (error) => {
      if (error) {
        resolve(null);
        return;
      }

      resolve(getCustomerPrivacyApi());
    });
  });
}

window.OnlyFindzConsent = window.OnlyFindzConsent || {
  get() {
    return ConsentStorage.get();
  },

  allows(category) {
    const consent = this.get();
    if (!consent) return false;
    return Boolean(consent[category]);
  },

  open() {},
};

if (!customElements.get('cookie-consent-banner')) {
  customElements.define(
    'cookie-consent-banner',
    class CookieConsentBanner extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.settingsPanel = this.querySelector('[data-consent-settings]');
        this.settingsToggle = this.querySelector('[data-consent-action="toggle-settings"]');
        this.checkboxes = Array.from(this.querySelectorAll('[data-consent-category]'));
        this.liveRegion = this.querySelector('[data-consent-live-region]');
        this.lastConsent = null;

        this.querySelectorAll('[data-consent-action]').forEach((button) =>
          button.addEventListener('click', () => this.handleAction(button.dataset.consentAction))
        );

        document.addEventListener('visitorConsentCollected', (event) => {
          const storedConsent = ConsentStorage.get();
          const consent = {
            preferences: Boolean(storedConsent && storedConsent.preferences),
            analytics: Boolean(event.detail && event.detail.analyticsAllowed),
            marketing: Boolean(event.detail && event.detail.marketingAllowed),
          };
          this.syncFromResolvedConsent(consent, { activateScripts: true, hideBanner: true });
        });

        document.querySelectorAll('[data-open-privacy-settings]').forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
            event.preventDefault();
            this.openPreferences();
          });
        });

        window.OnlyFindzConsent.open = () => this.openPreferences();

        this.initialize();
      }

      async initialize() {
        if (LOCAL_QA_CONSENT_OVERRIDE) {
          this.syncFromResolvedConsent(LOCAL_QA_CONSENT_OVERRIDE, { activateScripts: true, hideBanner: true });
          return;
        }

        /* The native Shopify Customer Privacy banner is the sole first-visit
           consent surface (region-aware via shouldShowBanner(), synced
           consent state). This element only ever shows on demand, via the
           footer "Cookie-Einstellungen verwalten" control, so it no longer
           competes with the native banner for screen space. */
        const api = await loadCustomerPrivacyApi();
        const platformConsent = this.readPlatformConsent(api);

        if (platformConsent) {
          this.syncFromResolvedConsent(platformConsent, { activateScripts: true, hideBanner: true });
        } else {
          const storedConsent = ConsentStorage.get();
          if (storedConsent) {
            this.syncFromResolvedConsent(storedConsent, { activateScripts: true, hideBanner: true });
          }
        }

        this.hidden = true;
      }

      readPlatformConsent(api) {
        if (!api || typeof api.currentVisitorConsent !== 'function') return null;
        return mapShopifyConsent(api.currentVisitorConsent());
      }

      handleAction(action) {
        if (action === 'accept-all') this.applyConsent({ preferences: true, analytics: true, marketing: true }, true);
        if (action === 'reject-all') this.applyConsent({ preferences: false, analytics: false, marketing: false }, true);
        if (action === 'toggle-settings') this.toggleSettings();
        if (action === 'save-preferences') {
          this.applyConsent(
            Object.fromEntries(
              this.checkboxes.map((checkbox) => [checkbox.dataset.consentCategory, checkbox.checked])
            ),
            true
          );
        }
      }

      toggleSettings(forceExpanded) {
        const currentlyExpanded = this.settingsToggle.getAttribute('aria-expanded') === 'true';
        const nextExpanded =
          typeof forceExpanded === 'boolean' ? forceExpanded : !currentlyExpanded;

        this.settingsToggle.setAttribute('aria-expanded', String(nextExpanded));
        this.settingsPanel.hidden = !nextExpanded;
      }

      async applyConsent(consent, persist) {
        const payload = normalizeConsent(consent);
        const api = await loadCustomerPrivacyApi();

        if (persist) {
          ConsentStorage.set(payload);
        }

        this.reflectConsent(payload);

        if (api && typeof api.setTrackingConsent === 'function') {
          api.setTrackingConsent(getTrackingConsentPayload(payload), () => {
            this.syncFromResolvedConsent(payload, { activateScripts: true, hideBanner: true });
          });
          return;
        }

        this.syncFromResolvedConsent(payload, { activateScripts: true, hideBanner: true });
      }

      syncFromResolvedConsent(consent, options = {}) {
        const payload = normalizeConsent(consent);

        ConsentStorage.set(payload);
        this.reflectConsent(payload);

        if (options.activateScripts) {
          this.activateDeferredScripts(payload);
        }

        if (!this.lastConsent || !consentMatches(this.lastConsent, payload)) {
          window.dispatchEvent(new CustomEvent('onlyfindz:consent-updated', { detail: payload }));
          this.lastConsent = payload;
        }

        if (options.hideBanner) {
          this.hidden = true;
          this.setAttribute('hidden', '');
        }
      }

      reflectConsent(consent) {
        this.checkboxes.forEach((checkbox) => {
          checkbox.checked = Boolean(consent[checkbox.dataset.consentCategory]);
        });
      }

      activateDeferredScripts(consent) {
        document.querySelectorAll('script[type="text/plain"][data-consent-category]').forEach((script) => {
          const category = script.dataset.consentCategory;
          if (!consent[category] || script.dataset.consentActivated === 'true') return;

          const executableScript = document.createElement('script');
          Array.from(script.attributes).forEach((attribute) => {
            if (attribute.name === 'type' || attribute.name === 'data-consent-activated') return;
            executableScript.setAttribute(attribute.name, attribute.value);
          });

          executableScript.textContent = script.textContent;
          script.dataset.consentActivated = 'true';
          script.replaceWith(executableScript);
        });
      }

      showBanner(openSettings) {
        this.hidden = false;
        this.removeAttribute('hidden');
        this.toggleSettings(Boolean(openSettings));

        if (this.liveRegion) {
          this.liveRegion.textContent = this.dataset.openLabel || '';
        }

        requestAnimationFrame(() => {
          const focusTarget = openSettings && this.checkboxes[0] ? this.checkboxes[0] : this.querySelector('[data-consent-action="accept-all"]');
          if (focusTarget) focusTarget.focus();
        });
      }

      openPreferences() {
        this.showBanner(true);
      }
    }
  );
}
